import {
  Iv2ARecipient,
  Maybe,
  TileStatus,
  Typenames,
  V2AInternalRecipient,
  V2ATransferAction,
  VaroToAnyoneTile,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import {
  AllRecipients,
  generateAvatarTheme,
  getAvatarInitials,
  shouldUseInitialsForAvatar,
} from 'features/v2a/sender/helpers/Sender.helpers';
import {
  Lavender,
  Size,
} from 'shared/components/vxl/avatar/UserAvatar/UserAvatar.helpers';
import {
  AvatarCardProps,
  AvatarWithLabelProps,
} from 'shared/components/vxl/AvatarCard/AvatarCard';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

interface VaroToAnyoneTileNode extends VXLComponent {
  id: string;
  name: VXLComponentId.AVATAR_CARD;
  props: AvatarCardProps;
}

export const varoToAnyoneTile = (
  tile: VaroToAnyoneTile,
  { varoToAnyoneTileClicked, translatedText }: HomeCardDependencies,
): Maybe<VaroToAnyoneTileNode> => {
  if (tile.status !== TileStatus.Loaded) {
    return null;
  }
  const topRecipients = tile.topRecipients;
  const avatarWithLabelProps: AvatarWithLabelProps[] =
    generateTopRecipientAvatars(
      topRecipients,
      (recipient) => recipient && varoToAnyoneTileClicked(recipient),
    );

  if (avatarWithLabelProps.length < 4) {
    avatarWithLabelProps.push(
      generateStaticAction(
        tile.staticActionCopy,
        translatedText('varoToAnyone.staticActionInitial'),
        () => varoToAnyoneTileClicked(null),
      ),
    );
  }

  const props: AvatarCardProps = {
    titleText: translatedText('varoToAnyone.title'),
    titlePressAction: () => varoToAnyoneTileClicked(null),
    isDisabled: false,
    avatarData: avatarWithLabelProps,
    analyticsId: 'receiverContactClicked',
  };

  return {
    id: tile.id,
    name: VXLComponentId.AVATAR_CARD,
    props,
  };
};

function generateStaticAction(
  labelText: string,
  initials: string,
  onPress: () => void,
): AvatarWithLabelProps {
  const prop: AvatarWithLabelProps = {
    labelText,
    onPress,
    analyticsId: 'newContactClicked',
    avatarProps: {
      id: labelText,
      name: VXLComponentId.USER_AVATAR,
      props: {
        userAvatarShowVaroLogo: false,
        userAvatarSize: Size.Medium,
        userAvatarVariant: {
          initials,
        },
        userAvatarTheme: Lavender, //TODO: - JT-120118
      },
    },
  };
  return prop;
}

function generateTopRecipientAvatars(
  transferActions: V2ATransferAction[],
  action: (recipient: AllRecipients | null) => void,
): AvatarWithLabelProps[] {
  if (transferActions.length === 0) {
    return [];
  }
  return transferActions
    .map((transferAction) => {
      const recipient: Iv2ARecipient = transferAction.recipient;
      const recipientInitials = generateInitials(recipient.displayName);

      const additionalProps = recipientInitials
        ? {
            userAvatarVariant: {
              initials: shouldUseInitialsForAvatar(recipientInitials)
                ? getAvatarInitials(recipientInitials)
                : '',
            },
            userAvatarTheme: shouldUseInitialsForAvatar(recipientInitials)
              ? generateAvatarTheme(recipient.displayName)
              : undefined,
          }
        : {};

      let contactType: string = '';
      if (recipient?.email?.address) {
        contactType = 'email';
      } else if (recipient?.phoneNumber) {
        contactType = 'phone';
      }

      const prop: AvatarWithLabelProps = {
        labelText: recipient.displayName,
        onPress: () => action(recipient),
        analyticsId: 'receiverContactClicked',
        customAttributes: {
          contact_type: contactType,
        },
        avatarProps: {
          id: recipient.id,
          name: VXLComponentId.USER_AVATAR,
          props: {
            userAvatarShowVaroLogo:
              (recipient as V2AInternalRecipient).__typename ===
              Typenames.V2AInternalRecipient,
            userAvatarSize: Size.Medium,
            ...additionalProps,
          },
        },
      };
      return prop;
    })
    .filter(isNotNil);
}

function generateInitials(name: string): string | undefined {
  //Check if name is empty or a phone number
  if (!name.trim() || name.charAt(0) === '+') {
    return undefined;
  }
  //split the name into parts (assuming parts are separated by spaces)
  const parts = name.trim().split(/\s+/);

  //extract initials from each part and join
  const initials = parts.map((part) => part.charAt(0).toUpperCase()).join('');
  return initials;
}
